/*
 * Copyright (c) 2023. Data Smart Technologies CC
 * https://www.datasmart.co.za
 * View license agreement and terms of use at https://www.datasmart.co.za/legal
 *
 * @author  Juan <juan@datasmart.co.za>
 * @version 1.0
 * @updated 2023-06-29
 */

import React from 'react';

// Utility type for a generic JSON object
type JsonObject = { [key: string]: any };

// Flatten a JSON object with nested fields
export const flattenJson = (obj: JsonObject, prefix = ''): JsonObject => {
  const flattened: JsonObject = {};

  if (obj) {
    Object.keys(obj).forEach((key) => {
      const value = obj[key];
      const prefixedKey = prefix ? `${prefix}.${key}` : key;

      if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
        // Recursively flatten nested objects
        Object.assign(flattened, flattenJson(value, prefixedKey));
      } else {
        flattened[prefixedKey] = value;
      }
    });
  }
  return flattened;
};

// Convert a JSON object to an HTML table with 2 columns: Names and Values
interface JsonToHtmlTableProps {
  jsonObj: JsonObject;
}


export const JsonToHtmlTable: React.FC<JsonToHtmlTableProps> = ({ jsonObj }) => {
  // Flatten the JSON object to simplify table structure
  const flattenedObj = flattenJson(jsonObj);

  // Start building the HTML table
  let html = '<table border="1" cellpadding="5" cellspacing="0" style="border-collapse:collapse;" width="100%">';

  // Add table headers
  html += '<tr><th>Name</th><th>Value</th></tr>';

  // Create rows for each key-value pair
  Object.entries(flattenedObj)
    .sort(([keyA], [keyB]) => keyA.localeCompare(keyB)) // Sort Alphabetically
    .forEach(([key, value]) => {
      let style = 'style="word-wrap: break-word; white-space: normal;"';
      let displayValue;

      if (Array.isArray(value)) {
        if (value.every(v => typeof v === 'object' && v !== null)) {
          // Array of objects: render nicely
          displayValue = `<pre>${JSON.stringify(value, null, 2)}</pre>`;
        } else {
          // Array of primitives
          displayValue = value.join(', ');
        }
      } else if (typeof value === 'object' && value !== null) {
        // Single object
        displayValue = `<pre>${JSON.stringify(value, null, 2)}</pre>`;
      } else {
        // Primitive
        displayValue = String(value);
      }

      html += `<tr><td ${style}>${key}</td><td>${displayValue}</td></tr>`;
    });

  // End table
  html += '</table>';

  return (
    <div>
      <div dangerouslySetInnerHTML={{ __html: html }} />
    </div>
  );
};
